import { v4 as uuidv4 } from 'uuid'
import throttle from 'lodash/throttle'
import isEmpty from 'lodash/isEmpty'

import { AnalyticsService } from '../../services/analytics.service'
import { CoverageTracker } from './coverageTracker'

const METRICS_DEVICE_ID_LOCAL_STORAGE_KEY = 'metricsDeviceId'

const RESET_VIDEO_SESSION_TIMEOUT = 300000 // 5 minutes

const createInitialSessionData = () => {
  return {
    slides: [],
    amountOfClicks: 0,
    durationWatchedHashMap: {},
    coverageTracker: new CoverageTracker(),
  }
}

const VIDEO_SESSION_SOURCE = {
  PUBLIC: 'public',
  EMBED: 'embed',
  SCORM: 'scorm',
  PREVIEW: 'preview',
}

export class AnalyticsCollector {
  #scorm = null
  #prevSlideId = null
  #videoSchedule = null
  #videoElement = null
  #currentSlideId = null
  #durationWatchedStartTimestamp = null
  #hasVideoQuizes = false

  #videoId = null
  #url = null
  #studentName = null
  #studentEmail = null
  #source = null // "public|embed|scorm|preview"

  // contains full data
  #scormSessionData = createInitialSessionData()
  #isScormSessionViewingFinished = false

  // can contain just part of scorm session data,
  // because video session can end earlier and start anew
  #videoSessionData = createInitialSessionData()
  #videoSessionId = null
  #resetVideoSessionTimeout = null
  #isVideoSessionViewingFinished = false
  #isVideoSessionFinished = false
  #videoSession5SecondsMark = false

  #completionRate = null
  #isCompletionRateScormTracking = false

  #updateVideoSessionMetricsThrottled = throttle(this.#updateVideoSessionMetrics, 300)
  #collectPlayingVideoMetricsThrottledMinute = throttle(this.#collectPlayingVideoMetrics, 60000)

  #deviceId = []

  constructor(scorm) {
    if (!scorm) return null
    this.#scorm = scorm
    this.isInitialized = false
    this.#isScormSessionViewingFinished = false
    this.hasQuiz = false

    this.#completionRate = window.videoData?.completionRate
    this.#isCompletionRateScormTracking = typeof this.#completionRate === 'number'
  }

  get #videoSessionMetrics() {
    if (!this.isInitialized) return null
    return {
      videoSessionId: this.#videoSessionId,
      hasQuiz: this.hasQuiz,
      videoId: this.#videoId,
      amountOfClicks: this.#videoSessionData.amountOfClicks,
      slides: this.#videoSessionData.slides,
      url: this.#url,
      studentName: this.#studentName,
      studentEmail: this.#studentEmail,
      deviceId: this.#deviceId,
      source: this.#source,
      coverage: this.#videoSessionData.coverageTracker.coverage,
    }
  }
  async init({ videoSchedule, videoElement, studentName, studentEmail }) {
    const videoId = window.videoData?.id ?? videoSchedule.videoId
    const url = window.location !== window.parent.location ? document.referrer : document.location.href
    this.#url = url
    this.#videoSchedule = videoSchedule
    this.#videoElement = videoElement
    this.#studentName = studentName
    this.#studentEmail = studentEmail
    this.#hasVideoQuizes = videoSchedule.hasVideoQuizes
    if (this.#scorm.enabled) {
      this.#studentName = this.#scorm.studentName
      this.#studentEmail = this.#scorm.studentEmail
    }
    this.#setupSource()
    // as some values may reset, recalculate anew
    this.#isScormSessionViewingFinished = false

    this.#videoSessionId = undefined
    this.#videoId = videoId

    const deviceId = localStorage.getItem(METRICS_DEVICE_ID_LOCAL_STORAGE_KEY)
    if (!deviceId) {
      try {
        this.#deviceId = uuidv4()
        localStorage.setItem(METRICS_DEVICE_ID_LOCAL_STORAGE_KEY, this.#deviceId)
      } catch (error) {
        console.error('Error while generating device id: ', error)
      }
    } else {
      this.#deviceId = deviceId
    }

    this.isInitialized = true

    // we can not track player visibility from iframe
    // so we need to init empty video session to track Impressions
    await this.#updateVideoSessionMetrics()
  }

  #setupSource() {
    if (this.#scorm.enabled) {
      this.#source = VIDEO_SESSION_SOURCE.SCORM
      return
    }

    const url = new URL(document.location.href)
    const paramsSource = url.searchParams.get('source')

    if (
      paramsSource &&
      [VIDEO_SESSION_SOURCE.PUBLIC, VIDEO_SESSION_SOURCE.EMBED, VIDEO_SESSION_SOURCE.PREVIEW].includes(paramsSource)
    ) {
      this.#source = paramsSource
      return
    }
  }

  #forEachSessionData(cb) {
    cb(this.#scormSessionData)
    cb(this.#videoSessionData)
  }

  #onNewVideoSessionCreated(newVideoSessionId) {
    // skip if exact session id is already set(in case it was race condition)
    if (newVideoSessionId && this.#videoSessionId && newVideoSessionId === this.#videoSessionId) {
      return
    }

    // if session id is empty - set it and keep existing data
    if (newVideoSessionId && !this.#videoSessionId) {
      this.#videoSessionId = newVideoSessionId
      return
    }

    this.#resetVideoSession(newVideoSessionId)
  }

  #updateScormQuizzesData() {
    if (!this.isInitialized) return

    const video = this.#videoSchedule.videoData
    const scormSessionSlides = this.#scormSessionData.slides

    if (!video || !scormSessionSlides || !this.#hasVideoQuizes) {
      return
    }

    // need to have right values and save it to lms
    const slidesWithCorrectAnswers = scormSessionSlides.filter((slide) => slide.isAnswerCorrect).length
    const slidesMeta = Object.values(this.#videoSchedule.slidesMetaHashMap)

    const slidesWithQuiz = slidesMeta.filter((slideMeta) => slideMeta.interactiveElementsOfSlide.hasQuizes).length

    this.#scorm.score = slidesWithCorrectAnswers / slidesWithQuiz
    this.#scorm.progress = scormSessionSlides.length / video.slides.length || 0
  }

  async #updateVideoSessionMetrics() {
    if (this.#source === VIDEO_SESSION_SOURCE.PREVIEW) return
    if (!this.isInitialized) return
    const videoSessionMetrics = this.#videoSessionMetrics
    const video = this.#videoSchedule.videoData
    if (!videoSessionMetrics) return
    if (!video) return

    if (this.#resetVideoSessionTimeout) {
      clearTimeout(this.#resetVideoSessionTimeout)
    }

    const hasUnansweredQuiz = this.#isCurrentSlideHasUnansweredQuiz()
    const isVideoSessionFinished = !hasUnansweredQuiz && this.#isVideoSessionViewingFinished
    if (isVideoSessionFinished) {
      this.#isVideoSessionFinished = true
    }

    // reset video session data if there is no activity for 5 minutes
    this.#resetVideoSessionTimeout = setTimeout(() => {
      this.#resetVideoSession()
      this.#resetVideoSessionTimeout = null
    }, RESET_VIDEO_SESSION_TIMEOUT)

    // axios throws unsafe header for keepalive
    const result = await AnalyticsService.sendVideoSessionMetrics(videoSessionMetrics)

    if (!result) {
      return
    }

    // if api created new session then we update data
    if (result.createNew === true) {
      if (this.#videoSessionId) {
        // reset session if we try to update finished session
        this.#onNewVideoSessionCreated(result.id)
      } else {
        // else just set new session id and keep already collected data
        this.#videoSessionId = result.id
      }
    }
  }

  #updateScormMetrics() {
    if (!this.isInitialized) return
    const video = this.#videoSchedule.videoData
    if (!video) return

    if (!this.#isCompletionRateScormTracking) {
      this.#updateScormQuizzesData()

      if (video.slides?.length && (this.#scormSessionData.slides.length || !this.#hasVideoQuizes)) {
        if (!this.#hasVideoQuizes) {
          this.#scorm.score = 1
          this.#scorm.completed = this.#isScormSessionViewingFinished ?? false
        } else {
          const hasUnansweredQuiz = this.#isCurrentSlideHasUnansweredQuiz()
          if (!hasUnansweredQuiz && this.#isScormSessionViewingFinished) {
            this.#scorm.completed = true
          }
        }
      }
      if (this.#scorm.completed) this.#scorm.close()
    }

    if (this.#isCompletionRateScormTracking) {
      const coverageRate = this.#videoSessionData.coverageTracker.coverage.coverageRate

      if (coverageRate >= this.#completionRate) {
        this.#scorm.score = 1
        this.#scorm.completed = true
      }

      if (this.#scorm.completed && this.#isScormSessionViewingFinished) {
        this.#scorm.close()
      }
    }
  }

  #updateMetrics() {
    this.#updateVideoSessionMetricsThrottled()
    this.#updateScormMetrics()
  }

  #setSlideDurationWatched(slideId) {
    this.#forEachSessionData((sessionData) => {
      let slide
      if (typeof slideId === 'number') slide = sessionData.slides.find(({ id }) => id === slideId)
      if (!slide) return
      const slideWatchedDurations = sessionData.durationWatchedHashMap[slide.id]
      if (!slideWatchedDurations) return
      slide.durationWatched = slideWatchedDurations.reduce((a, b) => a + b, 0)
    })
  }

  #updateDurationWatchedHashMap(time) {
    this.#forEachSessionData((sessionData) => {
      if (!this.#currentSlideId) return
      const current = sessionData.durationWatchedHashMap[this.#currentSlideId]
      if (!current) sessionData.durationWatchedHashMap[this.#currentSlideId] = [time]
      else current.push(time)
    })
  }

  #isCurrentSlideHasUnansweredQuiz() {
    const hasQuizes =
      !!this.#videoSchedule.slidesMetaHashMap[this.#currentSlideId]?.interactiveElementsOfSlide?.hasQuizes
    const hasAnswers = !!this.#scormSessionData.slides.find(({ id }) => id === this.#currentSlideId)?.answers?.length

    return hasQuizes && !hasAnswers
  }

  #resetVideoSession(newVideoSessionId) {
    if (!this.isInitialized) {
      return
    }
    this.#videoSessionId = newVideoSessionId || null
    this.#videoSessionData = createInitialSessionData()
    this.#isVideoSessionViewingFinished = false
    this.#isVideoSessionFinished = false
    this.#videoSession5SecondsMark = false
    if (this.#resetVideoSessionTimeout) {
      clearTimeout(this.#resetVideoSessionTimeout)
      this.#resetVideoSessionTimeout = null
    }
  }

  #collectPlayingVideoMetrics() {
    if (!this.#durationWatchedStartTimestamp) {
      return
    }

    this.stopTimer()
    this.startTimer()
  }

  setCurrentSlideId(slideId, isPaused) {
    if (!isPaused) {
      this.#collectPlayingVideoMetrics()
    }
    if (slideId === undefined || slideId === null) return
    if (slideId === this.#prevSlideId) return

    if (this.#prevSlideId) {
      this.#setSlideDurationWatched(this.#prevSlideId)
    }

    this.#currentSlideId = slideId
    this.#setSessionsSlides()
    this.#setSlideDurationWatched(slideId)
  }

  startTimer() {
    this.#durationWatchedStartTimestamp = Date.now()
    this.#forEachSessionData((sessionData) => {
      sessionData.coverageTracker.startRange({
        startSec: this.#videoElement?.currentTime,
      })
    })
  }

  stopTimer() {
    if (!this.#durationWatchedStartTimestamp) {
      return
    }
    const elapsedTime = (Date.now() - this.#durationWatchedStartTimestamp) / 1000
    this.#durationWatchedStartTimestamp = null
    this.#updateDurationWatchedHashMap(elapsedTime)
    if (!isEmpty(this.#videoSessionData.durationWatchedHashMap) && this.#currentSlideId) {
      this.#setSlideDurationWatched(this.#currentSlideId)
    }

    this.#forEachSessionData((sessionData) => {
      sessionData.coverageTracker.closeRange({
        videoDurationSec: this.#videoSchedule?.duration,
        currentTimeSec: this.#videoElement?.currentTime,
      })
    })

    this.#updateVideoSessionMetricsThrottled()

    if (this.#isCompletionRateScormTracking) {
      this.#updateScormMetrics()
    }
  }

  subscribeToVideoEvents(videoEl) {
    const startRange = () => {
      this.#forEachSessionData((sessionData) => {
        if (sessionData.coverageTracker) {
          sessionData.coverageTracker.startRange({ startSec: videoEl.currentTime })
        }
      })
    }

    const closeRange = () => {
      this.#forEachSessionData((sessionData) => {
        if (sessionData.coverageTracker) {
          sessionData.coverageTracker.closeRange({
            currentTimeSec: videoEl.currentTime,
            videoDurationSec: this.#videoSchedule?.duration,
          })
        }
      })
    }

    videoEl.addEventListener('playing', startRange)
    videoEl.addEventListener('pause', closeRange)
    videoEl.addEventListener('seeking', closeRange)
    videoEl.addEventListener('ended', closeRange)

    return () => {
      videoEl.removeEventListener('playing', startRange)
      videoEl.removeEventListener('pause', closeRange)
      videoEl.removeEventListener('seeking', closeRange)
      videoEl.removeEventListener('ended', closeRange)
    }
  }

  #setSessionSlide(sessionData) {
    if (!this.#currentSlideId) return

    const slideMetrics = sessionData.slides.find(({ id }) => id === this.#currentSlideId)
    if (!slideMetrics) {
      const slideData = this.#videoSchedule.slidesHashMap[this.#currentSlideId]
      const slideMetaData = this.#videoSchedule.slidesMetaHashMap[this.#currentSlideId]
      sessionData.slides.push({
        id: this.#currentSlideId,
        elementClicked: false,
        duration: slideData.duration,
        question: slideMetaData.interactiveElementsOfSlide.quizes[0]?.question.text,
      })
    }
  }

  #setSessionsSlides() {
    if (!this.#currentSlideId) return
    this.#isScormSessionViewingFinished = false
    this.#prevSlideId = this.#currentSlideId
    this.#forEachSessionData((sessionData) => {
      this.#setSessionSlide(sessionData)
    })
  }

  setClick() {
    this.#forEachSessionData((sessionData) => {
      const metricToUpdate = sessionData.slides.find(({ id }) => id === this.#currentSlideId)
      if (!metricToUpdate) return
      metricToUpdate.elementClicked = true
      sessionData.amountOfClicks++
      this.#updateMetrics()
    })
  }

  // call in in the Quiz component -> send quiz id
  setAnswerClick(userAnswers, result, quizId) {
    // should add some private methods to find it in better way
    const question = this.#videoSchedule.slidesMetaHashMap[this.#currentSlideId].interactiveElementsOfSlide.quizes.find(
      ({ id }) => id === quizId,
    ).question.text
    // select and narrow user answers to string values divided by coma
    // const selectedAnswers = // get it from params

    // get slide metrics via this.#slidesMap[id]
    this.#forEachSessionData((sessionData) => {
      sessionData.slides.forEach((slide) => {
        if (this.#currentSlideId === slide.id) {
          slide.answers = userAnswers
          slide.isAnswerCorrect = result
          if (question.text) {
            slide.question = question
          }
        }
      })
    })

    this.#scorm.interaction({
      id: this.#currentSlideId,
      description: question,
      // NOTE: now it operates with true/false values (correct/incorrect answer), but may be we should retrieve it from quiz? (text representation, not answer identifier)
      userResponse: String(result),
      result,
      correctResponse: 'true',
    })
    this.#updateMetrics()
  }

  collectWhenFinished() {
    // mark that video is ended which means we need to calculate results
    this.#isScormSessionViewingFinished = true
    this.#isVideoSessionViewingFinished = true
    this.saveBeforeUnload()
  }

  setHasQuiz() {
    this.hasQuiz = true
  }

  saveBeforeUnload() {
    // use scorm session data to get all slides(including video session slides)
    const slides = this.#scormSessionData.slides

    // update slides durations
    for (let index = 0; index < slides.length; index++) {
      const slide = slides[index]
      this.#setSlideDurationWatched(slide?.id)
    }
    this.#updateMetrics()
  }

  onCurrentTimeChange(event) {
    if (!this.#videoSchedule.duration) return

    const videoElement = event.target
    const currentTimeSec = event.target.currentTime
    const isPaused = videoElement.paused

    this.#forEachSessionData((sessionData) => {
      sessionData.coverageTracker.trackTimeChange({
        currentTimeSec: currentTimeSec,
        videoDurationSec: this.#videoSchedule?.duration,
        isPaused,
      })
    })

    // start new session if previous one is finished
    if (this.#isVideoSessionFinished) {
      // check if time is close to video end(1 second gap)
      // because it can be short playing after last quiz
      const isEndOfVideo =
        this.#videoSchedule.duration && currentTimeSec
          ? Math.abs(this.#videoSchedule.duration - currentTimeSec) < 1
          : false

      if (!isEndOfVideo) {
        this.#isVideoSessionFinished = false
        this.#isVideoSessionViewingFinished = false
        this.#resetVideoSession()
        this.#setSessionSlide(this.#videoSessionData)
      }
    }

    // if video passed 5 second - update video session metrics to track view
    if (!this.#videoSession5SecondsMark && currentTimeSec > 5 && !isPaused) {
      this.#videoSession5SecondsMark = true
      this.#collectPlayingVideoMetrics()
    }

    this.#collectPlayingVideoMetricsThrottledMinute()
  }
}
